import { computed, reactive, ref } from 'vue'

import * as api from '@/modules/members/api'
import * as organizationsApi from '@/modules/organizations/api'
import * as packagesApi from '@/modules/packages/api'
import { Member } from '@/modules/members/types'
import { SimpleListFilterParameters } from '@/views/_shared/search-list/types'

export * from './types'

const industrySectors = ['None', 'Accounting office', 'Company', 'Public', 'Other']
const groups = ['Test group']

const members = ref<Member[]>([])
const membersCount = ref<number>()
const member = ref<Member>()

const defaultSortBy = 'id desc'

export const membersFilter = reactive<SimpleListFilterParameters>({
    search: '',
    sortBy: null,
    limit: 10,
    offset: 0
})

const getMembers = () => {
    return api.getMembersList(
        membersFilter.search,
        membersFilter.sortBy || defaultSortBy,
        membersFilter.limit,
        membersFilter.offset
    ).then(
        membersList => {
            members.value = membersList.results
            membersCount.value = membersList.count
        }
    )
}

const addMemberToOrganization = async (memberId: string, organizationId: string) =>
    withMembersListUpdate(() =>
        organizationsApi.addMemberToOrganization(memberId, organizationId, {
            memberId,
            organizationId
        })
    )

const addMemberWithoutAuthenticationProfile = async (payload: api.AddMemberRequestWithoutPassword) => {
    if (payload.organizationId && payload.organizationId !== '') {
        const member = await api.addMemberWithoutAuthenticationProfile(payload) as { memberId: string}
        await addMemberToOrganization(member.memberId, payload.organizationId)
        return member
    } else {
        return await withMembersListUpdate(() => api.addMemberWithoutAuthenticationProfile(payload))
    }
}

const addMemberWithPasswordProfile = async (payload: api.AddMemberRequest) => {
    if (payload.organizationId && payload.organizationId !== '') {
        const member = await api.addMemberWithPasswordProfile(payload) as { memberId: string }
        await addMemberToOrganization(member.memberId, payload.organizationId)
        return member
    } else {
        return await withMembersListUpdate(() => api.addMemberWithPasswordProfile(payload))
    }
}

const deleteMember = (id: string) =>
    withMembersListUpdate(() => api.deleteSingleMember(id))

const findMembers = (phrase: string) =>
    withMembersListUpdate(() => api.findMember(phrase))

const getMember = async (id: string) => {
    member.value = await api.getMember(id)
    if (members.value.length) {
        const memberIndex = members.value.findIndex(value => value.id === id)
        members.value[memberIndex] = member.value
    }
    return member.value
}

const withMemberUpdate = async (memberId: string, callback: () => void) => {
    await callback()
    await getMember(memberId)
}

const withMembersListUpdate = async <T>(callback: () => Promise<T>) => {
    const result = await callback()
    await getMembers()
    return result
}

const updateMemberEmail = (id: string, payload: api.UpdateMemberEmailRequest) =>
    withMemberUpdate(id, () => api.updateSingleMemberEmail(id, payload))

const updateMemberName = (id: string, payload: api.UpdateMemberNameRequest) =>
    withMemberUpdate(id, () => api.updateSingleMemberName(id, payload))

const updateMemberPassword = (id: string, payload: api.UpdateMemberPasswordRequest) =>
    withMemberUpdate(id, () => api.updateSingleMemberPassword(id, payload))

const updateMemberPhone = (id: string, payload: api.UpdateMemberPhoneRequest) =>
    withMemberUpdate(id, () => api.updateSingleMemberPhone(id, payload))

const updateMemberJobTitle = (id: string, payload: api.UpdateMemberJobTitleRequest) =>
    withMemberUpdate(id, () => api.updateSingleMemberJobTitle(id, payload))

const updateMemberPreferredLanguage = (id: string, payload: api.UpdateMemberPreferredLanguageRequest) =>
    withMemberUpdate(id, () => api.updateSingleMemberPreferredLanguage(id, payload))

const updateMemberPermissionForMarketing = (id: string, payload: api.updateMemberPermissionForMarketingRequest) =>
    withMemberUpdate(id, () => api.updateSingleMemberPermissionForMarketing(id, payload))

const assignMemberToOrganization = (memberId: string, organizationId: string) =>
    withMemberUpdate(memberId, () => organizationsApi.addMemberToOrganization(memberId, organizationId, {
        organizationId: organizationId,
        memberId: memberId
    }))

const changeMemberOrganization = async (memberId: string, oldOrganizationId: string, newOrganizationId: string) =>
    await organizationsApi.deleteMemberFromOrganization(memberId, oldOrganizationId, {
        organizationId: oldOrganizationId,
        memberId: memberId
    }).then(() => {
        withMemberUpdate(memberId, () => organizationsApi.addMemberToOrganization(memberId, newOrganizationId, {
            organizationId: newOrganizationId,
            memberId: memberId
        }))
    })

const deleteMemberFromOrganization = async (memberId: string, organizationId: string) => {
    await withMemberUpdate(memberId, () => organizationsApi.deleteMemberFromOrganization(memberId, organizationId, {
        organizationId: organizationId,
        memberId: memberId
    }))
}

const updateMemberOrganization = async (memberId: string, oldOrganizationId: string | null, newOrganizationId: string) =>
    (oldOrganizationId === null)
        ? await assignMemberToOrganization(memberId, newOrganizationId)
        : await changeMemberOrganization(memberId, oldOrganizationId, newOrganizationId)

export function useMemberModule () {
    return {
        getMembers,
        members: computed(() => members.value),
        membersCount: computed(() => membersCount.value),
        member: computed(() => member.value),
        membersFilter,
        deleteMember,
        addMemberWithoutAuthenticationProfile,
        addMemberWithPasswordProfile,
        getMember,
        findMembers,
        industrySectors,
        groups,
        updateMemberEmail,
        updateMemberName,
        updateMemberPassword,
        updateMemberPhone,
        updateMemberJobTitle,
        updateMemberPreferredLanguage,
        updateMemberPermissionForMarketing,
        updateMemberOrganization,
        getMemberAccesses: api.getMemberAccesses,
        deleteMemberFromOrganization
    }
}
